import './DxcCreativeBuilder.scss';

import { Tab, TabNavigation } from '@hulu/react-style-components';
import { useFormikContext } from 'formik';
import { orderBy as _orderBy } from 'lodash';
import { bem } from 'mission-control-common-components';
import { useMemo } from 'react';

import type { Asset2Display, Asset2Text, Asset2Video } from '../../../../models';
import { isExtendedDisplayAsset, isExtendedTextAsset, isExtendedVideoAsset } from '../../../../utils/assets';
import Alert, { AlertType } from '../../../Alert';
import Loader from '../../../Loader';
import PreviewLinkButton from '../../../PreviewLinkButton';
import type { AdFormValues } from '../../adFormik';
import { CreativeFooter } from '../components';
import type { CreativeBuilderProps } from '../CreativeBuilder';
import CreativeBuilder from '../CreativeBuilder';
import { useCreativeData } from '../hooks/useCreativeData';
import { CreativeEditMode } from '../types';
import AssetSubTab from './AssetSubTab';
import { useDxcCreativeTabs } from './hooks';

const [block, element] = bem('dxc-creative-builder');

const CLASS_NAME_MODIFIER = 'dxc';

const DxcCreativeBuilder = ({ editMode }: CreativeBuilderProps): React.JSX.Element => {
  const {
    values: { creative, lineItem },
  } = useFormikContext<AdFormValues>();
  const { data, loading: loadingCreativeData, error: creativeDataError } = useCreativeData(
    creative,
    lineItem?.displayPublisherTarget
  );

  const sortedAssets = useMemo(() => _orderBy(data.assets, ['id', 'name'], 'desc'), [data.assets]);

  const categorizedAssets = useMemo(() => {
    const displayAssets: Asset2Display[] = [];
    const videoAssets: Asset2Video[] = [];
    const textAssets: Asset2Text[] = [];

    sortedAssets.forEach((asset) => {
      if (isExtendedDisplayAsset(asset)) {
        displayAssets.push(asset);
      } else if (isExtendedVideoAsset(asset)) {
        videoAssets.push(asset);
      } else if (isExtendedTextAsset(asset)) {
        textAssets.push(asset);
      }
    });

    return {
      displayAssets,
      videoAssets,
      textAssets,
    };
  }, [sortedAssets]);

  const { selectedTab, tabs, handleTabClick } = useDxcCreativeTabs({
    videoAssets: categorizedAssets.videoAssets,
    displayAssets: categorizedAssets.displayAssets,
    textAssets: categorizedAssets.textAssets,
    lineItemPublisherList: lineItem?.displayPublisherTarget || [],
  });

  const convertedEditMode = editMode === CreativeEditMode.readonly ? editMode : CreativeEditMode.partly;

  return (
    <CreativeBuilder
      lineItemPublishers={lineItem?.displayPublisherTarget || []}
      lineItemCountry={lineItem?.country}
      classNameModifier={CLASS_NAME_MODIFIER}
      editMode={convertedEditMode}
    >
      <div className={block()} data-testid="dxc-template">
        {loadingCreativeData && !creativeDataError ? (
          <div data-testid="assets-loader" className={element('loader')}>
            <Loader />
          </div>
        ) : (
          <>
            {creativeDataError && (
              <Alert type={AlertType.WARNING} bodyText="Unable to load asset previews. You can still edit this ad." />
            )}
            <div data-testid="asset-tabs" className={element('asset-tabs')}>
              <TabNavigation selectedTab={selectedTab} onTabClick={handleTabClick}>
                {tabs.map(
                  ({ key, title, element: componentElement, tabIndex, itemProps, previewElement, emptyMessage }) => (
                    <Tab key={key} title={title} element={componentElement} tabIndex={tabIndex}>
                      <AssetSubTab emptyMessage={emptyMessage} itemsProps={itemProps} previewElement={previewElement} />
                    </Tab>
                  )
                )}
              </TabNavigation>
            </div>
          </>
        )}
      </div>
      <CreativeFooter
        assets={sortedAssets}
        creative={creative}
        isLoading={loadingCreativeData}
        rightSideComponent={<PreviewLinkButton experienceId={creative.experienceId} />}
        lineItemData={lineItem}
      />
    </CreativeBuilder>
  );
};

export default DxcCreativeBuilder;
